// @leyro/api-client · createClient.js
// Factory dun cliente HTTP con auth + refresco automático de access token.
//
// Agnóstico de plataforma: a persistencia de tokens e o callback ao caducar
// sesión inxéctanse — web usa localStorage + redirect, mobile usa
// AsyncStorage + navigation.

import { ApiError } from "./errors.js";

/**
 * @typedef {Object} CreateClientOptions
 * @property {string} baseUrl                                         — ex. "https://api.leyro.es/api"
 * @property {import("./tokenStore.js").TokenStore} tokenStore        — adaptador
 * @property {() => void|Promise<void>} [onUnauthorized]              — callback cando o refresco falla
 * @property {string} [refreshEndpoint]                                — default "/auth/refresh"
 */

/**
 * @param {CreateClientOptions} options
 */
export function createApiClient({
  baseUrl,
  tokenStore,
  onUnauthorized,
  refreshEndpoint = "/auth/refresh",
}) {
  if (!baseUrl)    throw new Error("createApiClient: baseUrl is required");
  if (!tokenStore) throw new Error("createApiClient: tokenStore is required");

  // ── Coordinación de refresco — cola de peticións mentres se renova ──
  let isRefreshing = false;
  let refreshQueue = [];

  const processQueue = (error, token = null) => {
    refreshQueue.forEach(({ resolve, reject }) =>
      error ? reject(error) : resolve(token)
    );
    refreshQueue = [];
  };

  const refreshAccessToken = async () => {
    const refreshToken = await tokenStore.getRefresh();
    if (!refreshToken) throw new ApiError("No refresh token", { status: 401, code: "NO_REFRESH" });

    const res = await fetch(`${baseUrl}${refreshEndpoint}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refreshToken }),
    });

    if (!res.ok) {
      await tokenStore.clear();
      if (onUnauthorized) await onUnauthorized();
      throw new ApiError("Session expired", { status: 401, code: "REFRESH_FAILED" });
    }

    const data = await res.json();
    await tokenStore.set(data.accessToken, data.refreshToken);
    return data.accessToken;
  };

  // ── Petición ──────────────────────────────────────────────────────────
  const request = async (endpoint, options = {}) => {
    const url = `${baseUrl}${endpoint}`;
    const token = await tokenStore.getAccess();

    const config = {
      ...options,
      headers: {
        "Content-Type": "application/json",
        ...(token && { Authorization: `Bearer ${token}` }),
        ...options.headers,
      },
    };

    if (options.body && !(options.body instanceof FormData)) {
      config.body = JSON.stringify(options.body);
    }
    if (options.body instanceof FormData) {
      delete config.headers["Content-Type"];
    }

    let res;
    try {
      res = await fetch(url, config);
    } catch (e) {
      throw new ApiError(e?.message || "Network error", { status: 0 });
    }

    let prefetched = null;

    if (res.status === 401) {
      const data = await res.json().catch(() => ({}));
      if (data.code === "TOKEN_EXPIRED") {
        if (isRefreshing) {
          const newToken = await new Promise((resolve, reject) => {
            refreshQueue.push({ resolve, reject });
          });
          return request(endpoint, {
            ...options,
            headers: { ...options.headers, Authorization: `Bearer ${newToken}` },
          });
        }
        isRefreshing = true;
        try {
          const newToken = await refreshAccessToken();
          processQueue(null, newToken);
          config.headers.Authorization = `Bearer ${newToken}`;
          res = await fetch(url, config);
        } catch (err) {
          processQueue(err);
          throw err;
        } finally {
          isRefreshing = false;
        }
      } else {
        prefetched = data;
      }
    }

    let body;
    if (prefetched !== null) {
      body = prefetched;
    } else {
      const ct = res.headers.get("content-type");
      body = ct?.includes("application/json") ? await res.json() : await res.text();
    }

    if (!res.ok) {
      const firstValidationMsg = Array.isArray(body?.errors) && body.errors[0]
        ? (body.errors[0].msg || body.errors[0].message || null)
        : null;
      throw new ApiError(
        body?.error || body?.message || firstValidationMsg || `HTTP ${res.status}`,
        { status: res.status, code: body?.code, errors: body?.errors }
      );
    }
    return body;
  };

  // ── Descarga binaria (PDF/CSV) ────────────────────────────────────────
  const requestBinary = async (endpoint) => {
    const url = `${baseUrl}${endpoint}`;
    const token = await tokenStore.getAccess();
    const res = await fetch(url, {
      method: "GET",
      headers: { ...(token && { Authorization: `Bearer ${token}` }) },
    });
    if (!res.ok) {
      const errData = await res.json().catch(() => ({}));
      throw new ApiError(errData?.error || `HTTP ${res.status}`, { status: res.status });
    }
    const blob = await res.blob();
    const disposition = res.headers.get("content-disposition") || "";
    const match = disposition.match(/filename="?([^";\n]+)"?/);
    return { blob, filename: match ? match[1] : "descarga" };
  };

  // ── Helpers de método ─────────────────────────────────────────────────
  return {
    request,
    requestBinary,
    get:    (endpoint, options = {})       => request(endpoint, { method: "GET",    ...options }),
    post:   (endpoint, body, options = {}) => request(endpoint, { method: "POST",   body, ...options }),
    put:    (endpoint, body, options = {}) => request(endpoint, { method: "PUT",    body, ...options }),
    patch:  (endpoint, body, options = {}) => request(endpoint, { method: "PATCH",  body, ...options }),
    delete: (endpoint, options = {})       => request(endpoint, { method: "DELETE", ...options }),
    upload: (endpoint, formData) => request(endpoint, {
      method: "POST",
      body: formData,
      headers: {},
    }),
  };
}
